import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { ElectricityAgreementForm } from "../../models";
import { EnergyPackage } from "components/EnergyPackages/models";
import EnergyPackagesService from "components/EnergyPackages/energy-packages.service";
import EnergyAgreementService from "services/energy/energy-agreement.service";
import { EnergyAgreement } from "components/EnergyAgreements/model";
import { startDateInFuture } from "helpers";
import { dateFormatOptions } from "common/dateFormatterConfig";

interface NewElectricityAgreementEnergyPackageFormProps {
    form: ElectricityAgreementForm;
    setForm: Dispatch<SetStateAction<ElectricityAgreementForm>>;
}

export default function NewElectricityAgreementEnergyPackageForm(
    { form, setForm } : NewElectricityAgreementEnergyPackageFormProps
) {
    const [packages, setPackages] = useState<EnergyPackage[]>([]);
    const [agreements, setAgreements] = useState<EnergyAgreement[]>([])
    const [canSwitchPackage, setCanSwitchPackage] = useState(true);

    const hasAgreement = !!agreements.length;
    const defaultPackage = useMemo(
        () => packages.find(p => p.isDefault),
        [packages]
    );

    const packageOptions = useMemo(
        () => packages.filter(p => hasAgreement ? !p.isDefault: true).map(p => ({ value: p.id, label: p.nameEt })),
        [packages, hasAgreement]
    );

    const activeEnergyAgreement = useMemo(
        () => agreements.find(ag => !startDateInFuture(ag.startDate)),
        [agreements]
    );
    
    const futureEnergyAgreement = useMemo(
        () => agreements.find(ag => startDateInFuture(ag.startDate)),
        [agreements]
    );

    useEffect(() => {
        const fetchData = async () => {
            const packagesPromise = EnergyPackagesService.getAllEnergyPackages({ page: 0, size: 999 });

            if (form.customerId) {
                const [packagesData, agreementsData] = await Promise.all([
                    packagesPromise,
                    EnergyAgreementService.getCustomersEnergyAgreementsData(form.customerId)
                ]);

                setPackages(packagesData.content);
                setAgreements(agreementsData.agreements);
                setCanSwitchPackage(agreementsData.canSwitchPackage);
            } else {
                const packagesData = await packagesPromise;
                setPackages(packagesData.content);
            }
        };

    fetchData();
    }, [form.customerId]);

    useEffect(() => {
        const energyPackageId = activeEnergyAgreement?.energyPackageId || futureEnergyAgreement?.energyPackageId;

        setForm(prev => ({
            ...prev,
            energyPackageId: energyPackageId ?? defaultPackage?.id
        }));
    }, [activeEnergyAgreement, futureEnergyAgreement, defaultPackage, setForm])

    const currentPackageOption = useMemo(
        () => packageOptions.find(p => p.value === form.energyPackageId),
        [packageOptions, form.energyPackageId]
    );

    return (
        <div className="row">
            <div className="col-12 col-md-6">
                <Form.Label>Energiapakett*</Form.Label>
                    <Select
                        required
                        options={packageOptions}
                        value={currentPackageOption ?? null}
                        onChange={(option: any) => setForm({
                            ...form,
                            energyPackageId: packages.find(p => p.id === option?.value)?.id
                        })}
                        placeholder='Vali energiapakett'
                        className={'w-100 form-control form-select'}
                        isDisabled={!canSwitchPackage}
                    />
                    { !canSwitchPackage && (<div className="mt-1 text-warning">Energiapaketti saab vahetada kord kuus!</div>)}
            </div>
            <div className="col-12 col-md-6">
                { !!agreements.length && ( <div className="mb-2">Kliendi paketid</div> )}
                <EnergyAgreementCard
                    agreement={activeEnergyAgreement}
                    packageName={packageOptions.find(p => p.value === activeEnergyAgreement?.energyPackageId)?.label}
                    className={futureEnergyAgreement ? 'mb-2' : ''}
                />
                <EnergyAgreementCard
                    agreement={futureEnergyAgreement}
                    packageName={packageOptions.find(p => p.value === futureEnergyAgreement?.energyPackageId)?.label}
                />
            </div>
        </div>
    );
}

interface EnergyAgreementCardProps {
    agreement?: EnergyAgreement;
    packageName?: string;
    className?: string;
}
  
const EnergyAgreementCard = ({
    agreement,
    packageName,
    className
}: Readonly<EnergyAgreementCardProps> ) => {
    if(!agreement) return null;

    const startDateFormatted = new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(agreement.startDate));
    const endDateFormatted = agreement.endDate
      ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(agreement.endDate))
      : '-';
    
    return (
      <div className={`new-electricity-agreement__energy-agreement-card ${className ?? ''}`}>
        <div>{packageName}</div>
        <div>{startDateFormatted}</div>
        <div>{endDateFormatted}</div>
      </div>
    );
  };